import type { AnalyticsPayloadSchema } from "@Common/types/bo/analytics_public/analytics_public";
import { linkedInAuthClient } from "@Src/authProvider";
import { useLinkedIn } from "@Src/contexts/LinkedInContext";
import { sendEncryptedData } from "@Src/utils/encryption";
import { Box, Button, Paper, Typography } from "@mui/material";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import type { DataProvider } from "react-admin";

interface LinkedInWrapperProps {
	children: ReactNode;
	isLinkedInEnabled: boolean;
	dataProvider: DataProvider;
	recordId: string | null;
	publicKey: string | null;
}

export const LinkedInWrapper = ({
	children,
	isLinkedInEnabled,
	dataProvider,
	recordId,
	publicKey,
}: LinkedInWrapperProps) => {
	const [isChecking, setIsChecking] = useState(false);
	const [isSigningIn, setIsSigningIn] = useState(false);
	const [hasError, setHasError] = useState(false);
	const { isAuthenticated, setAuthState } = useLinkedIn();

	// Check authentication status on mount
	useEffect(() => {
		// Skip if LinkedIn is not enabled
		if (!isLinkedInEnabled) {
			return;
		}

		const checkAuth = async () => {
			setIsChecking(true);
			try {
				const { data: session } = await linkedInAuthClient.getSession();
				if (session?.user) {
					setAuthState(true, session.user);
					// Fire linkedin_auth_success event
					if (recordId && publicKey) {
						const payload: AnalyticsPayloadSchema = {
							type: "linkedin_auth_success",
							application_public_id: recordId,
						};
						sendEncryptedData(
							dataProvider,
							"analytics_public",
							recordId,
							payload,
							publicKey,
						).catch(() => {});
					}
				} else {
					setAuthState(false, null);
				}
			} catch (error) {
				console.error("LinkedIn authentication check failed:", error);
				setAuthState(false, null);
				setHasError(true);
			} finally {
				setIsChecking(false);
			}
		};

		// Only check if we haven't determined auth status yet
		if (isAuthenticated === null) {
			checkAuth();
		}
	}, [
		isLinkedInEnabled,
		isAuthenticated,
		setAuthState,
		recordId,
		publicKey,
		dataProvider,
	]);

	// Handle sign-in with LinkedIn
	const handleSignIn = useCallback(async () => {
		setIsSigningIn(true);
		setHasError(false);
		try {
			const result = await linkedInAuthClient.signIn.social({
				provider: "linkedin",
				callbackURL: window.location.href,
			});
			if (result.error) {
				console.error("LinkedIn sign-in error:", result.error);
				setHasError(true);
			}
		} catch (error) {
			console.error("LinkedIn sign-in failed:", error);
			setHasError(true);
		} finally {
			setIsSigningIn(false);
		}
	}, []);

	// If LinkedIn authentication is disabled, render children immediately
	if (!isLinkedInEnabled) {
		return <Box>{children}</Box>;
	}

	// Loading state while checking authentication
	if (isChecking) {
		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "#fafafa",
					p: 4,
				}}
			>
				<Paper sx={{ p: 4, textAlign: "center", maxWidth: 600 }}>
					<Typography variant="h5" gutterBottom>
						Checking Authentication
					</Typography>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						Please wait while we verify your LinkedIn authentication status.
					</Typography>
				</Paper>
			</Box>
		);
	}

	// Not authenticated - show sign-in prompt
	if (isAuthenticated === false) {
		return (
			<Box
				sx={{
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					backgroundColor: "#fafafa",
					p: 4,
				}}
			>
				<Paper sx={{ p: 4, textAlign: "center", maxWidth: 600 }}>
					<Typography variant="h5" gutterBottom>
						LinkedIn Authentication Required
					</Typography>
					<Typography variant="body2" color="text.secondary" gutterBottom>
						This form requires LinkedIn authentication to continue. Please sign
						in with your LinkedIn account to proceed.
					</Typography>
					{hasError && (
						<Typography
							variant="body2"
							color="error"
							gutterBottom
							sx={{ mt: 2 }}
						>
							An error occurred during sign-in. Please try again.
						</Typography>
					)}
					<Button
						variant="contained"
						color="primary"
						onClick={handleSignIn}
						sx={{ mt: 2 }}
						disabled={isSigningIn}
					>
						{isSigningIn ? "Signing in..." : "Sign in with LinkedIn"}
					</Button>
				</Paper>
			</Box>
		);
	}

	// Authenticated - render children
	return <Box>{children}</Box>;
};
